<script lang="ts">
	// Formating
	import { format } from 'svelte-i18n';
	// Design system components
	import {
		Alert,
		Button,
		Modal,
		Text,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system/index';
	// Helpers
	import { postReq } from 'src/services/http';
	import TemplateToggleSettings from '../Components/TemplateToggleSettings.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	// Types
	import type { Device } from 'src/types/Device';

	export let device: Device;

	let showModal = false;
	let wizardItems = [
		{
			titleKey: 'wizard_zones_title',
			title: 'Zones',
			subtitleKey: 'wizard_zones_subtitle',
			subtitle: 'Set Up Your Irrigation Zones',
			active: true,
			disabled: false,
			showMoreDots: false,
			stepNumber: 1,
			activeIndex: 0,
			bullets: [
				{
					key: 'wizard_zones_bullet_1',
					default:
						'Each zone can have individual settings for its irrigation duration and water consumption.',
				},
				{
					key: 'wizard_zones_bullet_2',
					default:
						"Set the duration in minutes to control how long the zone runs, and define the water amount in mm/m² per minute based on your sprinkler's specifications.",
				},
				{
					key: 'wizard_zones_bullet_3',
					default:
						'This water amount is essential for enabling accurate weather-based adjustments and tracking total water usage.',
				},
			],
		},
		{
			titleKey: 'wizard_sequences_title',
			title: 'Sequences',
			subtitleKey: 'wizard_sequences_subtitle',
			subtitle: 'Create and Manage Sequences',
			active: false,
			disabled: false,
			showMoreDots: false,
			stepNumber: 2,
			activeIndex: 1,
			bullets: [
				{
					key: 'wizard_sequences_bullet_1',
					default: 'Sequences let you define which zones to run and in what order.',
				},
				{
					key: 'wizard_sequences_bullet_2',
					default:
						'You can choose any number of zones, reorder them via drag-and-drop and give each sequence a custom name.',
				},
			],
		},
		{
			titleKey: 'wizard_weather_title',
			title: 'Weather',
			subtitleKey: 'wizard_weather_subtitle',
			subtitle: 'Enable Weather-Based Adjustments',
			active: false,
			disabled: false,
			showMoreDots: false,
			stepNumber: 3,
			activeIndex: 2,
			bullets: [
				{
					key: 'wizard_weather_bullet_1',
					default:
						"When enabled, the controller fetches temperature (6 h average) and rainfall (24 h total) from the nearest weather station. It automatically adjusts zone durations based on your configured base temperature and recent rainfall, using each zone's water consumption rate.",
				},
				{
					key: 'wizard_weather_bullet_2',
					default:
						'This ensures your irrigation adapts to real-world conditions and saves water.',
				},
			],
		},
	];

	let activeStep = wizardItems[0];
	const onClickWizardItem = (e, index: number) => {
		wizardItems = wizardItems.map((item, i) => {
			if (i === index) {
				activeStep = {
					...item,
					activeIndex: i,
				};

				return {
					...item,
					active: true,
				};
			}
			return {
				...item,
				active: false,
			};
		});
	};

	const onCloseWizard = async () => {
		const editedDevice = { ...device, quick_tour_guide: true };
		await postReq('interface/device/save', {
			id: editedDevice.id,
			data: JSON.stringify(editedDevice),
		});
		showModal = false;
	};
</script>

<Alert
	heading={$format('wizard_alert_heading', { default: 'Sequence Info' })}
	message={$format('wizard_alert_message', {
		default:
			'The device has not been configured yet. Please click on this card in order to properly set up your device.',
	})}
	cta={{
		title: $format('wizard_alert_cta', { default: 'Quick Tour Guide' }),
		enableAlertClick: false,
	}}
	on:cta_click={() => {
		showModal = true;
	}}
/>
<Modal
	on:close={() => (showModal = false)}
	{showModal}
	title={$format('wizard_modal_title', { default: 'Quick Start Tour' })}
	backdropClose={false}
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={(e) => {
					onClickWizardItem(e, i);
				}}
				active={item.active}
				title={$format(item.titleKey, { default: item.title })}
				disabled={item.disabled}
				showMoreDots={item.showMoreDots}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div slot="content">
		<WizardContainer activeStepIndex={activeStep.activeIndex}>
			{#each wizardItems as item, i (i)}
				<WizardContent active={activeStep.activeIndex === i}>
					<Text styleType="h2">
						{$format(item.subtitleKey, { default: item.subtitle })}
					</Text>

					{#if item.bullets}
						<ul class="wizard-bullets">
							{#each item.bullets as bullet}
								<li>{$format(bullet.key, { default: bullet.default })}</li>
							{/each}
						</ul>
					{/if}
					{#if i === 2}
						<TemplateToggleSettings
							{device}
							setting="weather_api"
							title={$format('label_weather_adjusted_irrigation', {
								default: 'Weather-adjusted irrigation',
							})}
						/>
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
		<div class="d-flex justify-content-end">
			{#if activeStep.activeIndex >= 1}
				<Button
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
					variant="secondary"
					responsiveSize={{
						sm: 'm',
					}}
					on:click={() => {
						onClickWizardItem(null, activeStep.activeIndex - 1);
					}}
				>
					{$format('button_back', { default: 'Back' })}
				</Button>
			{/if}
			<Button
				id="irrigationNextButton"
				aria-labelledby="irrigationNextButton"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={() => {
					if (activeStep.activeIndex < wizardItems.length - 1) {
						onClickWizardItem(null, activeStep.activeIndex + 1);
					} else {
						onCloseWizard();
					}
				}}
			>
				{#if activeStep.activeIndex < wizardItems.length - 1}
					{$format('button_next', { default: 'Next' })}
				{:else}
					{$format('button_close', { default: 'Close' })}
				{/if}
			</Button>
		</div>
	</div>
</Modal>

<style>
	.wizard-bullets {
		margin: 12px 0;
		padding-left: 20px;
		color: #e5e5e7;
	}

	.wizard-bullets li {
		margin-bottom: 8px;
		line-height: 1.4;
		font-size: 14px;
	}
</style>
